(function () {
  'use strict';

  const STORAGE_KEY = 'sensei_a11y';
  const defaults = {
    fontSize: 0,       // -2 to +4 steps
    contrast: '',      // '' | 'high' | 'inverted'
    grayscale: false,
    links: false,
    animations: false,
    dyslexic: false,
    cursor: false,
  };

  let state = Object.assign({}, defaults);

  /* ── Load saved state ────────────────────────────────────────────── */
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    Object.assign(state, saved);
  } catch (_) {}

  /* ── Apply helpers ───────────────────────────────────────────────── */
  const root = document.documentElement;

  function applyAll() {
    // Font size
    root.style.setProperty('--a11y-font-scale', 1 + state.fontSize * 0.1);
    // Contrast
    root.setAttribute('data-a11y-contrast', state.contrast);
    // Grayscale
    root.classList.toggle('a11y-grayscale', state.grayscale);
    // Links
    root.classList.toggle('a11y-links', state.links);
    // Animations
    root.classList.toggle('a11y-no-anim', state.animations);
    // Dyslexic
    root.classList.toggle('a11y-dyslexic', state.dyslexic);
    // Cursor
    root.classList.toggle('a11y-big-cursor', state.cursor);
  }

  function save() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) {}
  }

  function reset() {
    state = Object.assign({}, defaults);
    applyAll(); save(); renderPanel();
  }

  /* ── Build UI ────────────────────────────────────────────────────── */
  const css = `
    #snsei-a11y-btn {
      position: fixed;
      bottom: 24px;
      left: 24px;
      z-index: 99999;
      width: 52px; height: 52px;
      border-radius: 50%;
      background: #2C2C2C;
      color: #fff;
      border: none;
      cursor: pointer;
      box-shadow: 0 4px 16px rgba(0,0,0,.35);
      display: flex; align-items: center; justify-content: center;
      transition: transform .2s, bottom .3s cubic-bezier(0.4,0,0.2,1);
    }
    #snsei-a11y-btn:hover { transform: scale(1.08); }
    #snsei-a11y-btn svg { width: 28px; height: 28px; fill: #fff; pointer-events: none; }

    #snsei-a11y-panel {
      position: fixed;
      bottom: 86px;
      left: 24px;
      z-index: 99998;
      width: 300px;
      background: #fff;
      border-radius: 14px;
      box-shadow: 0 8px 40px rgba(0,0,0,.22);
      padding: 20px;
      direction: rtl;
      font-family: 'Noto Sans Hebrew', sans-serif;
      font-size: 14px;
      color: #222;
      display: none;
    }
    #snsei-a11y-panel.open { display: block; }

    #snsei-a11y-panel h3 {
      margin: 0 0 16px;
      font-size: 16px;
      font-weight: 600;
      border-bottom: 1px solid #eee;
      padding-bottom: 10px;
      text-align: right;
    }

    .a11y-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    }
    .a11y-row label { font-weight: 400; }

    .a11y-toggle {
      width: 44px; height: 24px;
      background: #ccc;
      border-radius: 12px;
      position: relative;
      cursor: pointer;
      border: none;
      transition: background .2s;
      flex-shrink: 0;
    }
    .a11y-toggle.on { background: #2C2C2C; }
    .a11y-toggle::after {
      content: '';
      position: absolute;
      top: 3px; right: 3px;
      width: 18px; height: 18px;
      border-radius: 50%;
      background: #fff;
      transition: transform .2s;
    }
    .a11y-toggle.on::after { transform: translateX(-20px); }

    .a11y-stepper {
      display: flex; gap: 6px;
    }
    .a11y-stepper button {
      width: 28px; height: 28px;
      border: 1px solid #ddd;
      border-radius: 6px;
      background: #f7f7f7;
      cursor: pointer;
      font-size: 16px; line-height: 1;
    }
    .a11y-stepper span {
      width: 28px; text-align: center; line-height: 28px;
      font-weight: 500;
    }

    .a11y-contrast-row {
      display: flex; gap: 6px; margin-bottom: 12px;
    }
    .a11y-contrast-row button {
      flex: 1;
      padding: 6px 4px;
      border: 2px solid #ddd;
      border-radius: 8px;
      cursor: pointer;
      font-size: 12px;
      background: #f7f7f7;
      font-family: 'Noto Sans Hebrew', sans-serif;
    }
    .a11y-contrast-row button.active { border-color: #2C2C2C; background: #2C2C2C; color: #fff; }

    .a11y-reset {
      width: 100%;
      margin-top: 14px;
      padding: 9px;
      border: 1px solid #ddd;
      border-radius: 8px;
      background: #f7f7f7;
      cursor: pointer;
      font-size: 13px;
      font-family: 'Noto Sans Hebrew', sans-serif;
    }
    .a11y-reset:hover { background: #eee; }

    /* ── Applied styles ── */
    html { font-size: calc(var(--a11y-font-scale, 1) * 62.5%) !important; }

    html[data-a11y-contrast="high"] body {
      filter: contrast(150%) !important;
    }
    html[data-a11y-contrast="inverted"] body {
      filter: invert(90%) hue-rotate(180deg) !important;
    }
    html.a11y-grayscale body { filter: grayscale(100%) !important; }
    html.a11y-links a {
      outline: 2px solid #e53 !important;
      background: rgba(238,85,51,.08) !important;
    }
    html.a11y-no-anim *, html.a11y-no-anim *::before, html.a11y-no-anim *::after {
      animation: none !important;
      transition: none !important;
    }
    html.a11y-dyslexic body {
      font-family: 'Arial', sans-serif !important;
      letter-spacing: 0.05em !important;
      word-spacing: 0.15em !important;
      line-height: 1.8 !important;
    }
    html.a11y-big-cursor, html.a11y-big-cursor * {
      cursor: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32'%3E%3Cpath fill='%23000' stroke='%23fff' stroke-width='2' d='M8 2 L8 26 L14 20 L18 30 L22 28 L18 18 L26 18Z'/%3E%3C/svg%3E") 0 0, auto !important;
    }
  `;

  function toggle(key) {
    state[key] = !state[key];
    applyAll(); save(); renderPanel();
  }

  function renderPanel() {
    const panel = document.getElementById('snsei-a11y-panel');
    if (!panel) return;

    const btn = (key, label) => {
      const on = state[key];
      return `<div class="a11y-row">
        <label>${label}</label>
        <button class="a11y-toggle ${on ? 'on' : ''}" data-toggle="${key}" aria-pressed="${on}" aria-label="${label}"></button>
      </div>`;
    };

    panel.innerHTML = `
      <h3><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16" style="display:inline-block;vertical-align:middle;margin-left:6px"><circle cx="12" cy="5" r="1"/><path d="M12 7v4m0 0l-3 5m3-5l3 5"/><path d="M9 11H6m6 0h3"/></svg> הגדרות נגישות</h3>

      <div class="a11y-row">
        <label>גודל טקסט</label>
        <div class="a11y-stepper">
          <button data-font="-1" aria-label="הקטן טקסט">−</button>
          <span>${state.fontSize >= 0 ? '+' : ''}${state.fontSize}</span>
          <button data-font="+1" aria-label="הגדל טקסט">+</button>
        </div>
      </div>

      <div class="a11y-row"><label>ניגודיות צבעים</label></div>
      <div class="a11y-contrast-row">
        <button data-contrast="" class="${state.contrast === '' ? 'active' : ''}">רגיל</button>
        <button data-contrast="high" class="${state.contrast === 'high' ? 'active' : ''}">גבוהה</button>
        <button data-contrast="inverted" class="${state.contrast === 'inverted' ? 'active' : ''}">הפוך</button>
      </div>

      ${btn('grayscale', 'גווני אפור')}
      ${btn('links', 'סימון קישורים')}
      ${btn('animations', 'עצור אנימציות')}
      ${btn('dyslexic', 'פונט לדיסלקטים')}
      ${btn('cursor', 'סמן גדול')}

      <button class="a11y-reset">↺ איפוס הגדרות</button>
    `;
  }

  /* ── Mount ───────────────────────────────────────────────────────── */
  function mount() {
    // Inject CSS
    const style = document.createElement('style');
    style.textContent = css;
    document.head.appendChild(style);

    // Floating button
    const btn = document.createElement('button');
    btn.id = 'snsei-a11y-btn';
    btn.setAttribute('aria-label', 'פתח תפריט נגישות');
    btn.setAttribute('aria-expanded', 'false');
    btn.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm-1 5h2l1 4 3-1 .5 2-3.5 1 .5 5h-1l-1-4-1 4H10l.5-5-3.5-1 .5-2 3 1 1-4z"/></svg>`;
    document.body.appendChild(btn);

    // Panel
    const panel = document.createElement('div');
    panel.id = 'snsei-a11y-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'תפריט נגישות');
    document.body.appendChild(panel);

    renderPanel();
    applyAll();

    // Toggle panel
    btn.addEventListener('click', () => {
      const open = panel.classList.toggle('open');
      btn.setAttribute('aria-expanded', String(open));
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!panel.contains(e.target) && !btn.contains(e.target)) {
        panel.classList.remove('open');
        btn.setAttribute('aria-expanded', 'false');
      }
    });

    // Event delegation inside panel
    panel.addEventListener('click', (e) => {
      const t = e.target;
      if (t.dataset.toggle) { toggle(t.dataset.toggle); return; }
      if (t.dataset.font) {
        state.fontSize = Math.max(-2, Math.min(4, state.fontSize + parseInt(t.dataset.font)));
        applyAll(); save(); renderPanel(); return;
      }
      if ('contrast' in t.dataset) {
        state.contrast = t.dataset.contrast;
        applyAll(); save(); renderPanel(); return;
      }
      if (t.classList.contains('a11y-reset')) { reset(); }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();

